/**
 * Drawer para elegir la velocidad de reproducción del audio de una historia.
 * Mismo patrón visual que WrongExplanationDrawer (bottom en móvil, right en
 * desktop). Presentacional: el estado de velocidad vive en StoryAudioContext
 * y lo aplica el KaraokePlayer, dueño del elemento <audio>.
 */

import { useTranslation } from 'react-i18next';
import { Check } from 'lucide-react';
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerBody,
  DrawerTitle,
} from '@/components/molecules/Drawer';
import { useIsMobile } from '@/hooks/useIsMobile';
import { cn } from '@/utils/cn';
import styles from './PlaybackSpeedDrawer.module.scss';

export interface PlaybackSpeedDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Velocidad actual (1 = normal). */
  speed: number;
  /** Velocidades disponibles (las mismas que en conversaciones). */
  options: readonly number[];
  /** Elegir una opción aplica la velocidad; el drawer permanece abierto. */
  onSpeedChange: (speed: number) => void;
}

export function PlaybackSpeedDrawer({
  open,
  onOpenChange,
  speed,
  options,
  onSpeedChange,
}: PlaybackSpeedDrawerProps) {
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const side = isMobile ? 'bottom' : 'right';

  return (
    <Drawer open={open} onOpenChange={onOpenChange}>
      <DrawerContent
        side={side}
        onSwipeToClose={isMobile ? () => onOpenChange(false) : undefined}
        closeSrLabel={t('header.close')}
        // Sin esto, Radix enfoca el botón que abrió el panel; en Safari/WebKit móvil el
        // siguiente toque no dispara click (parece «hay que pulsar varias veces»).
        onCloseAutoFocus={(e) => e.preventDefault()}
      >
        <DrawerHeader>
          <DrawerTitle>{t('story.playbackSpeedTitle')}</DrawerTitle>
        </DrawerHeader>
        <DrawerBody>
          <div
            className={styles.options}
            role="group"
            aria-label={t('story.playbackSpeedTitle')}
          >
            {options.map((s) => {
              const active = s === speed;
              return (
                <button
                  key={s}
                  type="button"
                  className={cn(styles.option, active && styles['option--active'])}
                  aria-pressed={active}
                  data-no-press-scale
                  onClick={() => onSpeedChange(s)}
                >
                  <span>{`${s}x`}</span>
                  {active && <Check size={18} aria-hidden />}
                </button>
              );
            })}
          </div>
        </DrawerBody>
      </DrawerContent>
    </Drawer>
  );
}

PlaybackSpeedDrawer.displayName = 'PlaybackSpeedDrawer';
